import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import {
  Loader2,
  Plus,
  AlertTriangle,
  Search,
  ShieldAlert,
  Users,
  ChevronLeft,
  Flag,
  Clock,
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ISSUE_TYPES = [
  { value: 'NON_PAYMENT', label: 'Non-payment' },
  { value: 'DANGEROUS_ANIMAL', label: 'Dangerous animal' },
  { value: 'NO_SHOW', label: 'No-show' },
  { value: 'UNSAFE_CONDITIONS', label: 'Unsafe conditions' },
  { value: 'DIFFICULT_CLIENT', label: 'Difficult client' },
  { value: 'OTHER', label: 'Other' },
];

const ISSUE_LABELS = ISSUE_TYPES.reduce((acc, t) => {
  acc[t.value] = t.label;
  return acc;
}, {});

export default function ProviderAlerts() {
  const { token } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [showAddReport, setShowAddReport] = useState(false);
  const [addReportText, setAddReportText] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);
  const [form, setForm] = useState({
    targetClientName: '',
    targetClientLocation: '',
    targetClientPhone: '',
    targetClientEmail: '',
    issueTypes: [],
    description: '',
  });

  useEffect(() => {
    loadAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAlerts = async (q = '') => {
    setLoading(true);
    try {
      const params = q ? `?search=${encodeURIComponent(q)}` : '';
      const res = await axios.get(`${API_URL}/api/provider-alerts${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlerts(res.data || []);
    } catch (e) {
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadAlerts(search);
  };

  const toggleIssueType = (val) => {
    setForm((f) => ({
      ...f,
      issueTypes: f.issueTypes.includes(val)
        ? f.issueTypes.filter((t) => t !== val)
        : [...f.issueTypes, val],
    }));
  };

  const submitAlert = async () => {
    if (!form.targetClientName || form.targetClientName.length < 2) {
      toast.error('Client name is required');
      return;
    }
    if (form.issueTypes.length === 0) {
      toast.error('Select at least one issue type');
      return;
    }
    if (!form.description || form.description.length < 10) {
      toast.error('Description must be at least 10 characters');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API_URL}/api/provider-alerts`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.action === 'added_to_existing') {
        toast.success('Added your report to an existing alert');
      } else {
        toast.success('Alert posted to community');
      }
      setShowCreate(false);
      setForm({
        targetClientName: '',
        targetClientLocation: '',
        targetClientPhone: '',
        targetClientEmail: '',
        issueTypes: [],
        description: '',
      });
      loadAlerts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post alert');
    } finally {
      setCreating(false);
    }
  };

  const submitAddReport = async () => {
    if (!addReportText || addReportText.length < 10) {
      toast.error('Description must be at least 10 characters');
      return;
    }
    setSubmittingReport(true);
    try {
      await axios.post(
        `${API_URL}/api/provider-alerts/${selectedAlert.id}/report`,
        { description: addReportText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Your report has been added');
      setShowAddReport(false);
      setAddReportText('');
      // Reload selected alert
      const res = await axios.get(`${API_URL}/api/provider-alerts/${selectedAlert.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedAlert(res.data);
      loadAlerts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add report');
    } finally {
      setSubmittingReport(false);
    }
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString([], {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '';
    }
  };

  // Detail view
  if (selectedAlert) {
    const isMultiple = selectedAlert.credibilityLevel === 'MULTIPLE_REPORTS';
    return (
      <div className="space-y-4" data-testid="alert-detail">
        <Button variant="ghost" onClick={() => setSelectedAlert(null)}>
          <ChevronLeft className="h-4 w-4 mr-2" /> Back to Alerts
        </Button>
        <Card className="border-yellow-300">
          <CardHeader>
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Badge className={isMultiple ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}>
                    {isMultiple ? (
                      <>
                        <Users className="h-3 w-3 mr-1" /> Multiple reports ({selectedAlert.reportCount})
                      </>
                    ) : (
                      <>
                        <ShieldAlert className="h-3 w-3 mr-1" /> Single report
                      </>
                    )}
                  </Badge>
                  {selectedAlert.status === 'DISPUTED' && (
                    <Badge variant="outline" className="text-orange-700 border-orange-300">Disputed</Badge>
                  )}
                </div>
                <CardTitle className="text-xl" data-testid="alert-client-name">
                  {selectedAlert.targetClientName}
                </CardTitle>
                {selectedAlert.targetClientLocation && (
                  <p className="text-sm text-muted-foreground mt-1">{selectedAlert.targetClientLocation}</p>
                )}
              </div>
              <Button onClick={() => setShowAddReport(true)} data-testid="add-report-btn">
                <Flag className="h-4 w-4 mr-2" /> Add Your Report
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {selectedAlert.issueTypes?.map((t) => (
                <Badge key={t} variant="secondary">
                  {ISSUE_LABELS[t] || t}
                </Badge>
              ))}
            </div>
            <div className="border-l-4 border-yellow-400 pl-4 py-2 bg-yellow-50">
              <p className="text-sm font-medium mb-1">
                {selectedAlert.reportingProviderName || 'Provider'}
                {selectedAlert.reportingBusinessName ? ` • ${selectedAlert.reportingBusinessName}` : ''}
                <span className="text-muted-foreground ml-2 font-normal">
                  • {formatDate(selectedAlert.createdAt)}
                </span>
              </p>
              <p className="whitespace-pre-wrap text-sm">{selectedAlert.description}</p>
            </div>

            {selectedAlert.additionalReports?.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-semibold text-sm">
                  Additional reports ({selectedAlert.additionalReports.length})
                </h4>
                {selectedAlert.additionalReports.map((r, i) => (
                  <div key={i} className="border-l-4 border-orange-300 pl-4 py-2 bg-orange-50">
                    <p className="text-sm font-medium mb-1">
                      {r.providerName || 'Provider'}
                      <span className="text-muted-foreground ml-2 font-normal">
                        • {formatDate(r.createdAt)}
                      </span>
                    </p>
                    <p className="whitespace-pre-wrap text-sm">{r.description}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={showAddReport} onOpenChange={setShowAddReport}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Your Report</DialogTitle>
            </DialogHeader>
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                Adding your report increases this alert's credibility. Only report based on
                first-hand experience. False reports may result in a mute.
              </AlertDescription>
            </Alert>
            <Label className="mt-2">Your description</Label>
            <Textarea
              rows={5}
              value={addReportText}
              onChange={(e) => setAddReportText(e.target.value)}
              placeholder="Describe your experience with this client..."
              maxLength={2000}
              data-testid="add-report-text"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddReport(false)}>
                Cancel
              </Button>
              <Button onClick={submitAddReport} disabled={submittingReport} data-testid="submit-add-report">
                {submittingReport && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Submit Report
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-4" data-testid="provider-alerts">
      <Alert className="bg-yellow-50 border-yellow-300">
        <ShieldAlert className="h-4 w-4 text-yellow-700" />
        <AlertDescription className="text-yellow-900">
          <strong>Provider safety:</strong> Use this space to warn other providers about non-paying,
          dangerous, or unsafe clients. <strong>Do not</strong> post defamatory content. False or
          malicious reports may result in account action.
        </AlertDescription>
      </Alert>

      <div className="flex flex-col sm:flex-row gap-3">
        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by client name or location..."
              className="pl-9"
              data-testid="alerts-search-input"
            />
          </div>
          <Button type="submit" variant="outline" data-testid="alerts-search-btn">Search</Button>
        </form>
        <Button onClick={() => setShowCreate(true)} data-testid="new-alert-btn">
          <Plus className="h-4 w-4 mr-2" /> New Alert
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : alerts.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <ShieldAlert className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No alerts found. Stay safe out there.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {alerts.map((alert) => {
            const isMultiple = alert.credibilityLevel === 'MULTIPLE_REPORTS';
            return (
              <Card
                key={alert.id}
                className={`cursor-pointer hover:border-primary transition-colors ${
                  isMultiple ? 'border-red-300' : 'border-yellow-300'
                }`}
                onClick={() => setSelectedAlert(alert)}
                data-testid={`alert-card-${alert.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <Badge className={isMultiple ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}>
                          {isMultiple ? (
                            <>
                              <Users className="h-3 w-3 mr-1" /> {alert.reportCount} reports
                            </>
                          ) : (
                            <>
                              <ShieldAlert className="h-3 w-3 mr-1" /> Single report
                            </>
                          )}
                        </Badge>
                        {alert.status === 'DISPUTED' && (
                          <Badge variant="outline" className="text-orange-700 border-orange-300">
                            Disputed
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-semibold">{alert.targetClientName}</h3>
                      {alert.targetClientLocation && (
                        <p className="text-xs text-muted-foreground">{alert.targetClientLocation}</p>
                      )}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {alert.issueTypes?.slice(0, 4).map((t) => (
                          <Badge key={t} variant="secondary" className="text-xs">
                            {ISSUE_LABELS[t] || t}
                          </Badge>
                        ))}
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2 mt-2">
                        {alert.description}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {formatDate(alert.createdAt)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Post a Provider Alert</DialogTitle>
          </DialogHeader>
          <Alert className="bg-yellow-50 border-yellow-300">
            <AlertTriangle className="h-4 w-4 text-yellow-700" />
            <AlertDescription className="text-sm text-yellow-900">
              Post only based on first-hand experience. Stick to facts. Defamatory or false alerts
              may result in your account being muted or suspended.
            </AlertDescription>
          </Alert>
          <div className="space-y-4">
            <div>
              <Label>Client Name *</Label>
              <Input
                value={form.targetClientName}
                onChange={(e) => setForm({ ...form, targetClientName: e.target.value })}
                placeholder="Full name of the client"
                maxLength={200}
                data-testid="alert-client-name-input"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Location (optional)</Label>
                <Input
                  value={form.targetClientLocation}
                  onChange={(e) => setForm({ ...form, targetClientLocation: e.target.value })}
                  placeholder="City, State"
                  maxLength={200}
                />
              </div>
              <div>
                <Label>Phone (optional)</Label>
                <Input
                  value={form.targetClientPhone}
                  onChange={(e) => setForm({ ...form, targetClientPhone: e.target.value })}
                  placeholder="(555) 555-5555"
                />
              </div>
            </div>
            <div>
              <Label>Email (optional)</Label>
              <Input
                value={form.targetClientEmail}
                onChange={(e) => setForm({ ...form, targetClientEmail: e.target.value })}
                placeholder="client@example.com"
              />
            </div>
            <div>
              <Label className="mb-2 block">Issue Types *</Label>
              <div className="grid grid-cols-2 gap-2">
                {ISSUE_TYPES.map((t) => (
                  <label
                    key={t.value}
                    className="flex items-center gap-2 cursor-pointer p-2 rounded border hover:bg-muted"
                  >
                    <Checkbox
                      checked={form.issueTypes.includes(t.value)}
                      onCheckedChange={() => toggleIssueType(t.value)}
                      data-testid={`issue-${t.value}`}
                    />
                    <span className="text-sm">{t.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Description * (min 10 chars)</Label>
              <Textarea
                rows={5}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Describe what happened. Stick to facts and your direct experience."
                maxLength={5000}
                data-testid="alert-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button onClick={submitAlert} disabled={creating} data-testid="submit-alert-btn">
              {creating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Post Alert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
